import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { useQuery } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { ReactQueryOptions, RouterOutputs, trpc } from "@utils/trpc";
import { NoAuthError } from "@utils/NoAuthError";
import { recommendedSongsListSchema } from "@libs/wire";
import type { z } from "zod";

export type RecommendedSongs =
  RouterOutputs["catalog"]["songs"]["listRecommendedSongs"];

type QueryOptions =
  ReactQueryOptions["catalog"]["songs"]["listRecommendedSongs"];

// Bidirectional drift assertion: schema and router output must agree.
type _RecommendedSongsWireSchemaSubset =
  z.infer<typeof recommendedSongsListSchema> extends RecommendedSongs
    ? true
    : never;
type _RecommendedSongsWireSchemaSuperset =
  RecommendedSongs extends z.infer<typeof recommendedSongsListSchema>
    ? true
    : never;
const _recommendedSongsWireSchemaDriftCheck: [
  _RecommendedSongsWireSchemaSubset,
  _RecommendedSongsWireSchemaSuperset,
] = [true, true];
void _recommendedSongsWireSchemaDriftCheck;

type RecommendedSongsParams = {
  limit?: number;
  lastFetchedSongId?: string;
  lastViewedSongId?: string;
};

export const recommendedSongsQuery = (
  data: DataSession,
  params?: RecommendedSongsParams,
) => {
  const queryKey = getQueryKey(trpc.catalog.songs.listRecommendedSongs, params);
  return data.cache.durableQueryOptions({
    queryKey,
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("recommendedSongsQuery queryFn");
      }
      return data.api.catalog.songs.listRecommendedSongs.query(params || {});
    },
    schema: recommendedSongsListSchema,
  });
};

export const recommendedSongsQueryOptions = (options?: QueryOptions) => {
  return {
    staleTime: 0, // Always fetch fresh on app launch — composes with initialData
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  };
};

export const useRecommendedSongs = (options?: QueryOptions) => {
  const data = useData();
  const query = useQuery({
    ...recommendedSongsQuery(data),
    ...recommendedSongsQueryOptions(options),
  });

  return query;
};
